import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import type { PrimerPastePlan } from '../../../Services/OrderWizard/primerPaste.ts'
import { pastePreview } from '../../../Services/OrderWizard/primerPaste.ts'
import { PRIMER_GRID_TITLES } from '../../../Services/OrderWizard/primerGrid.ts'
import type { PasteTarget } from '../../../Services/OrderWizard/primerPaste.ts'

/**
 * A block to paste, previewed before anything changes: matched rows or the cells it fills by position,
 * then Apply or Cancel. `support` is what to offer when the block is not a format the wizard knows.
 */
export const PrimerPastePanel = ({
  text,
  target,
  plan,
  currentSheet,
  support,
  onText,
  onApply,
  onCancel
}: {
  text: string
  target: PasteTarget
  plan: PrimerPastePlan | undefined
  currentSheet?: string
  support?: ReactNode
  onText: (text: string) => void
  onApply: () => void
  onCancel: () => void
}) => {
  const box = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    box.current?.focus()
    box.current?.scrollIntoView?.({ block: 'nearest' })
  }, [])
  const preview = plan ? pastePreview(plan, currentSheet) : undefined
  const applicable = plan?.kind === 'keyed' || plan?.kind === 'positional'
  const column = target.column === 'reverse' ? 'reverse' : 'forward'
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="wizard-primer-paste">
      <label className="block text-slate-700" htmlFor="wizard-primer-paste-box">
        Paste rows from your spreadsheet. With a header naming the samples (sample id, name or file), rows are matched by name; plain
        primers fill down from row {target.row + 1}, {PRIMER_GRID_TITLES[column].toLowerCase()}.
      </label>
      <textarea
        id="wizard-primer-paste-box"
        ref={box}
        aria-label="Pasted rows"
        className="textarea textarea-bordered w-full font-mono text-xs"
        rows={4}
        value={text}
        onChange={(event) => onText(event.target.value)}
      />
      {preview && (
        <div data-testid="wizard-paste-preview">
          <p className={applicable ? 'font-medium text-slate-900' : 'font-medium text-amber-700'}>{preview.headline}</p>
          {preview.notes.length > 0 && (
            <ul className="list-inside list-disc text-xs text-slate-600">
              {preview.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {support}
      <div className="flex gap-2">
        <button type="button" className="btn btn-primary btn-sm" disabled={!applicable} data-testid="wizard-paste-apply" onClick={onApply}>
          Apply
        </button>
        <button type="button" className="btn btn-ghost btn-sm" data-testid="wizard-paste-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}
